import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { config } from '~/config';
import UserService from '~/services/user.service';
import { useAuth } from '~/context/auth';

interface ActivitiesAccessContextType {
  hasFullAccess: boolean | null;
  onlyQuizAccess: boolean | null;
  onlyDashboardAccess: boolean | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

const ActivitiesAccessContext = createContext<ActivitiesAccessContextType | undefined>(undefined);

interface AccessState {
  hasFullAccess: boolean;
  onlyQuizAccess: boolean;
  onlyDashboardAccess: boolean;
}

function fetchAccess(): Promise<AccessState> {
  return UserService.getAccess().then((r) => ({
    hasFullAccess: r.hasFullAccess,
    onlyQuizAccess: r?.onlyQuizAccess === true,
    onlyDashboardAccess: r?.onlyDashboardAccess === true,
  }));
}

export function ActivitiesAccessProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [hasFullAccess, setHasFullAccess] = useState<boolean | null>(null);
  const [onlyQuizAccess, setOnlyQuizAccess] = useState<boolean | null>(null);
  const [onlyDashboardAccess, setOnlyDashboardAccess] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);

  const shouldFetch = isAuthenticated && config.features.quizEnabled;

  const load = useCallback(async () => {
    if (!shouldFetch) {
      setHasFullAccess(null);
      setOnlyQuizAccess(null);
      setOnlyDashboardAccess(null);
      setIsLoading(false);
      return;
    }

    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);

    try {
      const access = await fetchAccess();
      if (mountedRef.current) {
        setHasFullAccess(access.hasFullAccess);
        setOnlyQuizAccess(access.onlyQuizAccess);
        setOnlyDashboardAccess(access.onlyDashboardAccess);
      }
    } catch {
      if (mountedRef.current) {
        setHasFullAccess(false);
        setOnlyQuizAccess(false);
        setOnlyDashboardAccess(false);
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
      fetchingRef.current = false;
    }
  }, [shouldFetch]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refetch = useCallback(async () => {
    if (!shouldFetch) return;
    await load();
  }, [shouldFetch, load]);

  const value: ActivitiesAccessContextType = {
    hasFullAccess,
    onlyQuizAccess,
    onlyDashboardAccess,
    isLoading,
    refetch,
  };

  return (
    <ActivitiesAccessContext.Provider value={value}>{children}</ActivitiesAccessContext.Provider>
  );
}

export function useActivitiesAccess(): ActivitiesAccessContextType {
  const context = useContext(ActivitiesAccessContext);
  if (context === undefined) {
    throw new Error('useActivitiesAccess deve ser usado dentro de ActivitiesAccessProvider');
  }
  return context;
}
